import { type AddToCartFormData, type AddToCartHookProps, useAddToCartForm } from "../../hooks/useAddToCartNew";
import {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { useIsMinDesktopSize } from "../../../common/hooks/useMediaQuery";
import { useTranslation, type WithTranslation } from "react-i18next";
import { Button } from "../../../common/components/Button/Button";
import classNames from "classnames";
import { userIsDistributor } from "../../../utils/roles";
import { IconType } from "../../../common/components/icon/IconType";
import { getNetPrice, isProductPMA } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { FormProvider } from "react-hook-form";
import { LoadingModal } from "./Modals/LoadingModal";
import { ErrorLoadingModal } from "./Modals/ErrorLoadingModal";
import { ContractAvailableModal } from "./Modals/ContractAvailableModal";
import { AddToCartModal } from "./Modals/AddToCartModal";
import { ErrorSendingModal } from "./Modals/ErrorSendingModal";
import { ConsumerViewAvailabilityModal } from "./Modals/ConsumerViewAvailabilityModal";
import { KITSummaryModal } from "./Modals/KITSummaryModal";
import { ProductService } from "../../ProductService";
import { selectProperAmountInCart } from "../../redux/cartAmounts/cartAmountSelectors";
import { isKITProduct } from "../../../models/ProductData";
import { getIsContractAvailable } from "../../../ProductDetails/redux/productDetailsSelectors";
import { Icon } from "~/common/components/icon/Icon";
import { SalesDataProvider } from "~/SalesCampaign/useSalesData";
import { calculateSalesCampaign4PDP } from "~/SalesCampaign/utils";
import { getStore } from "~/redux/getStore";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

export type AddToCartSubmitHandler = (descriptor: ProductDescriptor, data: AddToCartFormData) => Promise<boolean>;

export interface AddToCartWizardProps extends AddToCartHookProps {
  restrictionQuery: string;
  closePopup: (openPreviousModal?: boolean) => void;
  onSubmit: AddToCartSubmitHandler;
}

export enum AddToCartStep {
  LOADING,
  ERROR_LOADING,
  CONTRACT_AVAILABLE,
  LOADED_CONSUMER, // TODO: Get rid of this...
  LOADED_KIT,
  LOADED_DEFAULT,
  ERROR_SENDING,
}

export const AddToCartWizard = ({ restrictionQuery, closePopup, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const [formProps, productDescriptor] = useAddToCartForm(hookProps);
  const editMode = hookProps.editMode;

  const [step, setStep] = useState<AddToCartStep>(() => getInitialStep(productDescriptor));
  const [isSending, setIsSending] = useState(false);

  // Ensure we cycle through initial loading states to a loaded state
  useLayoutEffect(() => {
    setStep(getInitialStep(productDescriptor));
  }, [productDescriptor]);

  const isLoadedState = [AddToCartStep.LOADED_KIT, AddToCartStep.LOADED_DEFAULT].includes(step);

  const submit = async () => {
    setIsSending(true);
    const formData = { ...formProps.getValues() };
    const priceReference = formData.priceReference;
    Object.keys(formData.optionalParts).forEach((id) => {
      const part = { ...formData.optionalParts[id] };
      formData.optionalParts[id] = part;
      const { current: descriptor } = ProductService.getProductDescriptor(part.productId, priceReference);
      const totalQuantity =
        part.quantity +
        selectProperAmountInCart({
          productCode: descriptor.base.code,
          packCode: part.selectedPackCode,
          priceReference,
        })(getStore().getState());
      const price = getNetPrice({ product: descriptor, packCode: part.selectedPackCode, totalQuantity });
      part.netPrice = price;
      part.priceUnit = price?.unit ?? 1;
    });

    const result = await onSubmit(productDescriptor, formData);

    if (result) {
      closePopup();
      setIsSending(false);
    } else {
      setIsSending(false);
      setStep(AddToCartStep.ERROR_SENDING);
    }
  };

  useLayoutEffect(() => {
    if (!productDescriptor.base.quantityVisible && isLoadedState) {
      submit();
    }
  }, [isLoadedState, productDescriptor.base.quantityVisible]);

  const wizardContextProps = {
    closePopup: () => closePopup(step === AddToCartStep.LOADED_KIT),
    editMode: editMode ?? false,
    restrictionQuery,
    step,
    setStep,
    productDescriptor,
  };

  const salesData = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);

  return (
    <SalesDataProvider value={salesData.data}>
      <WizardContextProvider {...wizardContextProps}>
        <FormProvider {...formProps}>
          {step === AddToCartStep.LOADING && <LoadingModal />}
          {step === AddToCartStep.ERROR_LOADING && <ErrorLoadingModal productDescriptor={productDescriptor} />}
          {step === AddToCartStep.CONTRACT_AVAILABLE && (
            <ContractAvailableModal productDescriptor={productDescriptor} />
          )}
          {step === AddToCartStep.LOADED_CONSUMER && (
            <ConsumerViewAvailabilityModal productDescriptor={productDescriptor} />
          )}
          {step === AddToCartStep.LOADED_KIT && (
            <KITSummaryModal productDescriptor={productDescriptor} submit={submit} />
          )}
          {step === AddToCartStep.LOADED_DEFAULT && (
            <AddToCartModal productDescriptor={productDescriptor} submit={submit} sending={isSending} />
          )}
          {step === AddToCartStep.ERROR_SENDING && <ErrorSendingModal productDescriptor={productDescriptor} />}
        </FormProvider>
      </WizardContextProvider>
    </SalesDataProvider>
  );
};

type WizardContextProviderProps = Pick<
  AddToCartWizardContext,
  "setStep" | "editMode" | "restrictionQuery" | "closePopup"
> & {
  productDescriptor: ProductDescriptor;
  step: AddToCartStep;
  children?: ReactNode;
};
export const WizardContextProvider: FC<WizardContextProviderProps> = ({
  children,
  setStep,
  editMode,
  restrictionQuery,
  closePopup,
  productDescriptor,
  step,
}) => {
  const isMinDesktop = useIsMinDesktopSize();
  const { t } = useTranslation();

  const isLoadedState = [AddToCartStep.LOADED_KIT, AddToCartStep.LOADED_DEFAULT].includes(step);

  const contextData: AddToCartWizardContext = useMemo(() => {
    return {
      restrictionQuery,
      closePopup,
      editMode: editMode ?? false,
      setStep,
      isMinDesktop,
      t,
      getModalProps: () => getModalProps(),
      getMainCls: getMainCls,
      getCloseBtn: (label: ReactNode, fullWidth?: boolean) =>
        getCloseBtn(label, fullWidth ?? !isMinDesktop, closePopup),
      getActionBtn: (
        label: ReactNode,
        action: string | OnClickAction,
        disabled?: boolean,
        id?: string,
        fullWidth?: boolean,
      ) =>
        getActionBtn(label, fullWidth ?? !isMinDesktop, closePopup, action, productDescriptor, isLoadedState, disabled),
    };
  }, [setStep, editMode, restrictionQuery, closePopup, isMinDesktop, t, isLoadedState, productDescriptor]);

  return <Context.Provider value={contextData}>{children}</Context.Provider>;
};

function getInitialStep(productDescriptor: ProductDescriptor): AddToCartStep {
  if (
    userHasPermission(Permission.AVAILABILITY) &&
    !userHasPermission(Permission.PRICE) &&
    productDescriptor.availability
  ) {
    return AddToCartStep.LOADED_CONSUMER;
  }
  if (productDescriptor.isLoaded) {
    if (getIsContractAvailable(productDescriptor)) {
      return AddToCartStep.CONTRACT_AVAILABLE;
    }
    return decideLoadedStep(productDescriptor);
  }
  if (productDescriptor.isError) {
    return AddToCartStep.ERROR_LOADING;
  }
  return AddToCartStep.LOADING;
}

export function decideLoadedStep(productDescriptor: ProductDescriptor): AddToCartStep {
  if (isKITProduct(productDescriptor.base)) {
    const { includedParts, optionalParts } = productDescriptor.base;
    if (includedParts.length || optionalParts.length) {
      return AddToCartStep.LOADED_KIT;
    }
  }
  return AddToCartStep.LOADED_DEFAULT;
}

type OnClickAction = () => void;

interface AddToCartWizardContext {
  restrictionQuery: string;
  closePopup: (openPreviousModal?: boolean) => void;
  setStep: Dispatch<SetStateAction<AddToCartStep>>;
  editMode: boolean;
  isMinDesktop: boolean;
  t: WithTranslation["t"];
  getMainCls: () => string;
  getModalProps: () => {
    modalClassName: string;
  };
  getCloseBtn: (label: ReactNode, fullWidth?: boolean) => ReactNode;
  getActionBtn: (
    label: ReactNode,
    action: string | OnClickAction,
    disabled?: boolean,
    id?: string,
    fullWidth?: boolean,
  ) => ReactNode;
}

const Context = createContext<AddToCartWizardContext>({
  restrictionQuery: "",
  closePopup: () => {},
  setStep: () => {},
  editMode: false,
  isMinDesktop: false,
  // eslint-disable-next-line
  t: (() => null) as any,
  getMainCls: () => "",
  getModalProps: () => ({
    modalClassName: "",
  }),
  getCloseBtn: () => null,
  getActionBtn: () => null,
});

export const useAddToCartWizardContext = () => useContext(Context);

function getModalProps() {
  return {
    modalClassName: `scha__add-to-cart-popup`,
  };
}

function getMainCls() {
  return classNames("scha__atc-popup", userIsDistributor() ? "scha__atc-popup--distributor" : "");
}

function getCloseBtn(label: ReactNode, fullWidth: boolean, onClose: () => void) {
  return (
    <Button
      variant={"text"}
      onClick={onClose}
      fullWidth={fullWidth}
      data-test-id={"add-to-cart-btn-cancel"}
      clta-modal-child-marker="close"
    >
      {label}
    </Button>
  );
}

function getActionBtn(
  label: ReactNode,
  fullWidth: boolean,
  onClose: () => void,
  action: string | OnClickAction,
  descriptor: ProductDescriptor,
  isLoadedState: boolean,
  disabled?: boolean,
) {
  disabled = window.SCHA.config.disableAllWS || (disabled == null ? false : disabled);
  if (typeof action === "string") {
    return (
      <Button
        href={action}
        onClick={(event: React.MouseEvent) => {
          if (window.location.origin + window.location.pathname === action) {
            event.preventDefault();
            onClose();
          }
        }}
        size={"medium"}
        startIcon={<Icon type={IconType.Cart} />}
        disabled={disabled}
        data-test-id={"add-to-cart-btn-ok"}
        fullWidth={fullWidth}
      >
        {label}
      </Button>
    );
  } else {
    let buttonIcon = IconType.Cart;
    if (isProductPMA(descriptor) && isLoadedState) {
      buttonIcon = IconType.FileList;
    }

    return (
      <Button
        onClick={() => action()}
        size={"medium"}
        startIcon={<Icon type={buttonIcon} />}
        disabled={disabled}
        data-test-id={"add-to-cart-btn-ok"}
        fullWidth={fullWidth}
      >
        {label}
      </Button>
    );
  }
}
